import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import { Images } from "../../../utils/Images";
import { subscribeNewsletter } from "../../../api/services/newsletterService";

const NewsLetter = () => {
  // Custom responsive breakpoints
  const isTablet = useMediaQuery("(min-width: 701px) and (max-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 700px)");
  
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', or 'info'
  });

  const handleSubscribe = async () => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await subscribeNewsletter(email);
      // Axios interceptor returns response.data directly, so response is already the data object
      // Log for debugging (remove in production)
      console.log("Newsletter API Response:", response);
      
      const responseData = response || {};
      
      // Check if subscription was successful
      if (responseData.success === true) {
        setSnackbar({
          open: true,
          message: responseData.message || "Successfully subscribed to our newsletter!",
          severity: "success",
        });
        setEmail(""); // Clear the input on success
      } else if (responseData.success === false) {
        // Already subscribed or other failure with success: false
        const message = responseData.message || "You are already subscribed to our newsletter.";
        const isAlreadySubscribed = message.toLowerCase().includes("already subscribed");
        
        setSnackbar({
          open: true,
          message: message,
          severity: isAlreadySubscribed ? "info" : "error",
        });
      } else {
        // If response doesn't have success field, assume success if we got here (200 status)
        // This handles cases where API might return data directly without success field
        if (responseData.message) {
          setSnackbar({
            open: true,
            message: responseData.message,
            severity: "success",
          });
          setEmail("");
        } else {
          setSnackbar({
            open: true,
            message: "Successfully subscribed to our newsletter!",
            severity: "success",
          });
          setEmail("");
        }
      }
    } catch (error) {
      // Log for debugging (remove in production)
      console.log("Newsletter API Error:", error);
      
      // Handle HTTP errors (4xx, 5xx)
      // Error handler transforms error to {status, message}, but original data might be in error.response.data
      const errorResponse = error?.response;
      const errorData = errorResponse?.data || error?.data || {};
      const errorMessage = errorData.message || error?.message || "Failed to subscribe. Please try again.";
      
      // Check if it's an already subscribed error from the API
      const messageLower = errorMessage.toLowerCase();
      if (errorData.success === false || messageLower.includes("already subscribed")) {
        setSnackbar({
          open: true,
          message: errorData.message || errorMessage,
          severity: "info",
        });
      } else {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading) {
      handleSubscribe();
    }
  };

  return (
    <Box
      sx={{
        width: "94.44%",
        maxWidth: "1440px",
        // minHeight: { xs: '200px', sm: '250px', md: '286px' },
        backgroundColor: "#F04951",
        borderRadius: { xs: "15px", sm: "18px", md: "20px" },
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 auto",
        position: "relative",
        padding: { xs: "16px", sm: "0px", md: "0" },
        // Responsive gap between boxes
        gap: { xs: "20px", sm: "40px", md: "60px" },
        // Custom mobile styling for screens ≤700px
        "@media (max-width: 700px)": {
          padding: "16px",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "0px",
        },
      }}
    >
      {/* Yellow Hearts - Top Right */}
      <Box
        component="img"
        src={Images.yellowHearts}
        alt="Yellow Hearts"
        sx={{
          position: "absolute",
          top: { xs: "25px", sm: "35px", md: "47px" },
          right: { xs: "45px", sm: "55px", md: "71px" },
          width: { xs: "35px", sm: "42px", md: "52px" },
          height: { xs: "36px", sm: "43px", md: "53px" },
          zIndex: 4,
          // Custom responsive for 765px - 1100px screens
                "@media (min-width: 700px)":{
                  display: "none",
                },
          // Custom responsive for 700px - 1100px screens
          "@media (min-width: 320px) and (max-width: 699px)": {
            top: "30px",
            right: "50px",
            width: "45px",
            height: "46px",
            transform: "rotate(-70deg)"
          },
        }}
      />
       {/* String Hand positioned above the mask */}
            <Box
              component="img"
              src={Images.stringHand}
              alt="String Hand"
              sx={{
                width: { xs: "45px", sm: "60px", md: "96px" },
                height: { xs: "38px", sm: "50px", md: "82px" },
                position: "absolute",
                top: { xs: "40px", sm: "55px", md: "106px" },
                left: { xs: "120px", sm: "160px", md: "283px" },
                zIndex: 4,
                transform: "scaleX(-1)",
                // Custom responsive for 765px - 1100px screens
                "@media (min-width: 700px)":{
                  display: "none",
                },
                "@media (min-width: 320px) and (max-width: 699px)": {
                  width: "40px",
                  height: "58px",
                  top: "0px",
                  right: "0",
                  left: "unset",
                   transform: "scaleX(1)",
                },
              }}
            />
      {/* Decorative Elements - Always visible */}
      {/* White Heart - Upper Left */}
      <Box
        component="img"
        src={Images.whiteHeart}
        alt="White Heart"
        sx={{
          position: "absolute",
          top: { xs: "30px", sm: "40px", md: "58px" },
          left: { xs: "30px", sm: "45px", md: "68px" },
          width: { xs: "16px", sm: "18px", md: "22px" },
          height: { xs: "17px", sm: "19px", md: "23px" },
          zIndex: 4,
          transform: "rotate(303.51deg)",
          // Hide below 700px
          "@media (max-width: 700px)": {
            display: "none",
          },
          // Custom responsive for 700px - 1100px screens
          "@media (min-width: 700px) and (max-width: 1100px)": {
            top: "23px",
            left: "20px",
            width: "20px",
            height: "21px",
          },
        }}
      />

      {/* White Hearts - Lower Left */}
      <Box
        component="img"
        src={Images.whiteHearts}
        alt="White Hearts"
        sx={{
          position: "absolute",
          bottom: { xs: "15px", sm: "20px", md: "26px" },
          right: { xs: "120px", sm: "140px", md: "170px" },
          width: { xs: "35px", sm: "42px", md: "52px" },
          height: { xs: "36px", sm: "43px", md: "53px" },
          zIndex: 4,
          // Hide below 700px
          "@media (max-width: 700px)": {
            display: "none",
          },
          // Custom responsive for 700px - 1100px screens
          "@media (min-width: 700px) and (max-width: 1100px)": {
            bottom: "-7px",
            right: "150px",
            width: "30px",
            height: "46px",
          },
        }}
      />

      {/* Yellow Hearts - Top Right */}
      <Box
        component="img"
        src={Images.yellowHearts}
        alt="Yellow Hearts"
        sx={{
          position: "absolute",
          top: { xs: "25px", sm: "35px", md: "47px" },
          right: { xs: "45px", sm: "55px", md: "71px" },
          width: { xs: "35px", sm: "42px", md: "52px" },
          height: { xs: "36px", sm: "43px", md: "53px" },
          zIndex: 4,
          // Hide below 700px
          "@media (max-width: 700px)": {
            display: "none",
          },
          // Custom responsive for 700px - 1100px screens
          "@media (min-width: 700px) and (max-width: 1100px)": {
            top: "30px",
            right: "60px",
            width: "45px",
            height: "46px",
          },
        }}
      />

      {/* White Heart - Bottom Right */}
      <Box
        component="img"
        src={Images.whiteHeart}
        alt="White Heart"
        sx={{
          position: "absolute",
          bottom: { xs: "35px", sm: "42px", md: "52px" },
          left: { xs: "70px", sm: "85px", md: "102px" },
          width: { xs: "16px", sm: "18px", md: "22px" },
          height: { xs: "17px", sm: "19px", md: "23px" },
          zIndex: 4,
           transform: "rotate(303.51deg)",
          // Hide below 700px
          "@media (max-width: 700px)": {
            display: "none",
          },
          // Custom responsive for 700px - 1100px screens
          "@media (min-width: 700px) and (max-width: 1100px)": {
            bottom: "45px",
            left: "36px",
            width: "20px",
            height: "21px",
          },
        }}
      />

      {/* Left Box - Hidden on Mobile */}
      {!isMobile && (
        <Box
          sx={{
            width: { xs: "100%", sm: "40%", md: "42%" },
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            // Custom responsive for 765px - 1100px screens
            "@media (min-width: 700px) and (max-width: 1100px)": {
              width: "auto",
            },
          }}
        >
          {/* Girl Image with CSS Mask */}
          <Box
            sx={{
              width: { xs: "160px", sm: "200px", md: "284px" },
              height: { xs: "160px", sm: "200px", md: "285px" },
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // Custom responsive for 765px - 1100px screens
              "@media (min-width: 700px) and (max-width: 1100px)": {
                width: "180px",
                height: "180px",
              },
            }}
          >
            {/* Girl Image with mask applied */}
            <Box
              component="img"
              src={Images.girlMask}
              alt="Girl Mask"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                maskImage: `url(${Images.maskDesign})`,
                maskSize: "100% 100%",
                maskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskImage: `url(${Images.maskDesign})`,
                WebkitMaskSize: "100% 100%",
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
              }}
            />

            {/* Girl Mask Top Image positioned above the mask */}
            <Box
              component="img"
              src={Images.girlMaskTop}
              alt="Girl Mask Top"
              sx={{
                width: { xs: "120px", sm: "150px", md: "234px" },
                height: { xs: "120px", sm: "150px", md: "234px" },
                objectFit: "cover",
                position: "absolute",
                top: { xs: "-25px", sm: "-30px", md: "-49.49px" },
                left: { xs: "20px", sm: "25px", md: "44px" },
                zIndex: 3,
                // Custom responsive for 765px - 1100px screens
                "@media (min-width: 700px) and (max-width: 1100px)": {
                  width: "140px",
                  height: "140px",
                  top: "-24px",
                  left: "31px",
                },
              }}
            />

            {/* String Hand positioned above the mask */}
            <Box
              component="img"
              src={Images.stringHand}
              alt="String Hand"
              sx={{
                width: { xs: "45px", sm: "60px", md: "96px" },
                height: { xs: "38px", sm: "50px", md: "82px" },
                position: "absolute",
                top: { xs: "40px", sm: "55px", md: "106px" },
                left: { xs: "120px", sm: "160px", md: "283px" },
                zIndex: 4,
                transform: "scaleX(-1)",
                // Hide below 700px
                "@media (max-width: 700px)": {
                  display: "none",
                },
                // Custom responsive for 765px - 1100px screens
                "@media (min-width: 700px) and (max-width: 1100px)": {
                  width: "40px",
                  height: "58px",
                  top: "66px",
                  left: "178px",
                },
              }}
            />
          </Box>
        </Box>
      )}

      {/* Right Box - Full width on mobile, reduced on tablet */}
      <Box
        sx={{
          width: { xs: "100%", sm: "55%", md: "50%" },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: {
            xs: "flex-start",
            sm: "flex-start",
            md: "flex-start",
          },
          alignItems: { xs: "flex-start", sm: "flex-start", md: "flex-start" },
          textAlign: { xs: "left", sm: "left", md: "left" },
          // Custom responsive for 765px - 1100px screens
          "@media (min-width: 700px) and (max-width: 1100px)": {
            width: "auto",
          },
          // Custom mobile styling for screens ≤700px
          "@media (max-width: 700px)": {
            justifyContent: "flex-start",
            alignItems: "flex-start",
            textAlign: "left",
            width: "100%",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontStyle: "normal",
            fontSize: { xs: "20px", sm: "24px", md: "28px" },
            lineHeight: { xs: "24px", sm: "28px", md: "32px" },
            letterSpacing: "0%",
            color: "white",
            textAlign: { xs: "left", sm: "left", md: "left" },
            paddingBottom: { xs: "8px", sm: "10px", md: "12px" },
            // Custom responsive for 765px - 1100px screens
            "@media (min-width: 765px) and (max-width: 1100px)": {
              fontSize: "22px",
              lineHeight: "26px",
              paddingBottom: "10px",
            },
          }}
        >
          Subscribe Our Newsletter
        </Typography>

        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: { xs: "12px", sm: "13px", md: "14px" },
            lineHeight: { xs: "14px", sm: "15px", md: "16px" },
            letterSpacing: "0%",
            color: "white",
            textAlign: { xs: "left", sm: "left", md: "left" },
            paddingBottom: { xs: "20px", sm: "22px", md: "26px" },
            // Custom responsive for 765px - 1100px screens
            "@media (min-width: 700px) and (max-width: 1100px)": {
              fontSize: "12px",
              lineHeight: "14px",
              paddingBottom: "20px",
            },
          }}
        >
          Join us in spreading hope
        </Typography>

        {/* Email Input Field with Submit Button */}
        <Box
          sx={{
            width: { xs: "100%", sm: "350px", md: "448px" },
            height: { xs: "44px", sm: "46px", md: "48px" },
            backgroundColor: "white",
            borderRadius: { xs: "8px", sm: "9px", md: "10px" },
            padding: { xs: "5px", sm: "6px", md: "6px 6px 6px 19px" },
            display: "flex",
            alignItems: "center",
            // Custom responsive for 765px - 1100px screens
            "@media (min-width: 700px) and (max-width: 1100px)": {
              width: "380px",
              height: "45px",
              maxWidth: "380px",
            },
            // Custom mobile styling for screens ≤700px
            "@media (max-width: 700px)": {
              width: "100%",
              maxWidth: "none",
            },
          }}
        >
          <TextField
            placeholder="Enter your Email address"
            variant="standard"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            InputProps={{
              disableUnderline: true,
              style: {
                fontFamily: "Red Hat Display",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: { xs: "11px", sm: "11.5px", md: "12px" },
                lineHeight: { xs: "17px", sm: "18px", md: "19.2px" },
                letterSpacing: "0px",
                textAlign: "center",
                color: "#979796",
                height: { xs: "34px", sm: "35px", md: "36px" },
                marginLeft: { xs: "10px", sm: "12px", md: "13px" },
                padding: 0,
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: { xs: "34px", sm: "35px", md: "36px" },
                display: "flex",
                alignItems: "center",
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: { xs: "11px", sm: "11.5px", md: "12px" },
                fontFamily: "Red Hat Display",
                fontWeight: 400,
                color: "#686866",
                opacity: 1,
              },
            }}
          />

          <Button
            variant="contained"
            onClick={handleSubscribe}
            disabled={loading}
            sx={{
              width: { xs: "70px", sm: "80px", md: "88px" },
              height: { xs: "34px", sm: "35px", md: "36px" },
              backgroundColor: "#1D1D1B",
              borderRadius: { xs: "5px", sm: "6px", md: "6px" },
              fontFamily: "Red Hat Display",
              fontWeight: 600,
              fontStyle: "normal",
              fontSize: { xs: "11px", sm: "11.5px", md: "12px" },
              lineHeight: { xs: "13px", sm: "13.5px", md: "14px" },
              letterSpacing: "0%",
              textAlign: "center",
              color: "#FFFFFF",
              textTransform: "none",
              minWidth: { xs: "70px", sm: "80px", md: "88px" },
              "&:hover": {
                backgroundColor: "#2D2D2B",
              },
              "&:disabled": {
                backgroundColor: "#999999",
                color: "#FFFFFF",
              },
            }}
          >
            {loading ? "..." : "Submit"}
          </Button>
        </Box>
      </Box>
      
      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsLetter;
